/**
 * StockTab — Vista consolidada de stock por bodega y producto.
 * Incluye filtros por bodega, búsqueda, y alerta de stock bajo.
 */
import { useState } from 'react'
import { Package, AlertTriangle, Search, Eye, MapPin } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { useAllStock, useWarehouses, type WarehouseStock } from '../hooks/useInventory'
import { useTenantLocale, formatNumber } from '../../../lib/locale'
import { ToastContainer, useToast } from '../../../components/Toast'
import StockDetailModal from './StockDetailModal'
import { Pagination } from '@/components/ds/Pagination'

export default function StockTab() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [detail, setDetail] = useState<{ id: string; name: string; sku: string } | null>(null)
  const locale = useTenantLocale()
  const fmtQty = (v: string) => formatNumber(parseFloat(v), locale, parseFloat(v) % 1 !== 0 ? 2 : 0)
  const fmtMoney = (v: number) => `$${formatNumber(v, locale, v % 1 !== 0 ? 2 : 0)}`
  const [warehouseId, setWarehouseId] = useState('')
  const [lowStock, setLowStock] = useState(false)
  const toast = useToast()

  const { data: whData } = useWarehouses({ active: 'true', page_size: 9999 })
  const { data, isLoading } = useAllStock({
    search: search || undefined,
    warehouse_id: warehouseId || undefined,
    low_stock: lowStock ? 'true' : undefined,
    page,
    page_size: 20,
  })

  const warehouses = whData?.warehouses || []
  const stock = data?.stock || []
  const total = data?.count || 0

  return (
    <>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-subtle" />
          <input aria-label="Buscar SKU o nombre"
            type="text"
            placeholder="Buscar SKU o nombre..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary-200"
          />
        </div>
        <select aria-label="Todas las bodegas"
          value={warehouseId}
          onChange={e => { setWarehouseId(e.target.value); setPage(1) }}
          className="px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content"
        >
          <option value="">Todas las bodegas</option>
          {warehouses.map(w => (
            <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-content-muted cursor-pointer">
          <input
            type="checkbox"
            checked={lowStock}
            onChange={e => { setLowStock(e.target.checked); setPage(1) }}
            className="rounded border-divider-strong"
          />
          <AlertTriangle size={14} className="text-warning-solid" />
          Stock bajo
        </label>
      </div>

      {/* Table */}
      <DataTable
        columns={[
          {
            key: 'product_name', header: 'Producto',
            render: (s: WarehouseStock) => {
              const bajo = parseFloat(s.reorder_point) > 0 && parseFloat(s.quantity) <= parseFloat(s.reorder_point)
              return (
                <span className="font-medium text-content">
                  {s.product_name}
                  {bajo && <AlertTriangle size={14} className="inline ml-2 text-warning-solid" />}
                </span>
              )
            },
          },
          { key: 'product_sku', header: 'SKU', render: (s: WarehouseStock) => <span className="font-mono">{s.product_sku}</span> },
          { key: 'warehouse_code', header: 'Bodega', render: (s: WarehouseStock) => (s as any).warehouse_code },
          {
            key: 'location_code', header: 'Ubicación',
            render: (s: WarehouseStock) => (s as any).location_code
              ? <span className="inline-flex items-center gap-1 text-xs"><MapPin size={10} /> {(s as any).location_code}</span>
              : '—',
          },
          { key: 'quantity', header: 'Cantidad', numeric: true, sortValue: (s: WarehouseStock) => parseFloat(s.quantity), render: (s: WarehouseStock) => fmtQty(s.quantity) },
          { key: 'reserved_quantity', header: 'Reservado', numeric: true, render: (s: WarehouseStock) => fmtQty(s.reserved_quantity) },
          {
            key: 'available_quantity', header: 'Disponible', numeric: true,
            render: (s: WarehouseStock) => <span className="font-medium text-primary">{fmtQty(s.available_quantity)}</span>,
          },
          { key: 'avg_cost', header: 'Costo Prom.', numeric: true, sortValue: (s: WarehouseStock) => parseFloat(s.avg_cost), render: (s: WarehouseStock) => fmtMoney(parseFloat(s.avg_cost)) },
          {
            key: 'valor', header: 'Valor Total', numeric: true,
            sortValue: (s: WarehouseStock) => parseFloat(s.quantity) * parseFloat(s.avg_cost),
            render: (s: WarehouseStock) => <span className="font-medium">{fmtMoney(parseFloat(s.quantity) * parseFloat(s.avg_cost))}</span>,
          },
        ]}
        rows={stock}
        rowKey={(s: WarehouseStock) => s.id}
        // El stock bajo el punto de reposición se atenúa la fila ENTERA: hacerlo
        // celda por celda deja el fondo y los bordes sin marcar.
        rowClassName={(s: WarehouseStock) =>
          parseFloat(s.reorder_point) > 0 && parseFloat(s.quantity) <= parseFloat(s.reorder_point)
            ? 'bg-amber-50/50 dark:bg-amber-900/20'
            : undefined}
        onRowClick={(s: WarehouseStock) => setDetail({ id: s.product_id, name: s.product_name, sku: s.product_sku })}
        loading={isLoading}
        empty={<EmptyState icon={AlertTriangle} size="sm" title="Sin stock registrado." />}
        minWidth={1100}
        caption="Stock por bodega"
      />

      {/* Pagination */}
      {total > 20 && (
        <Pagination
          page={page}
          pageSize={20}
          total={total}
          onPageChange={setPage}
          locale={locale}
        />
      )}

      {detail && (
        <StockDetailModal
          productId={detail.id}
          productName={detail.name}
          productSku={detail.sku}
          onClose={() => setDetail(null)}
        />
      )}

      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />
    </>
  )
}
